import { Dialog } from "@kobalte/core/dialog"
import { Button } from "@opencode/ui/button"
import { Icon } from "@opencode/ui/icon"
import { IconButton } from "@opencode/ui/icon-button"
import { Tooltip } from "@opencode/ui/tooltip"
import { Show } from "solid-js"
import { createStore } from "solid-js/store"
import { Portal } from "solid-js/web"
import "./sidebar/surfaces.css"
import { useLanguage } from "@/runtime/i18n/language"
import { useSettingsSurface } from "@/settings/surface"
import { SairAccountBar } from "@/sair/gate"
import type { LayoutRoute } from "@/shell/state/layout"
import { SidebarCollections } from "./sidebar/collections"

export function SairNavigation(props: {
  expanded: boolean
  route: LayoutRoute["type"]
  historyOpen: boolean
  onHistoryChange: (open: boolean) => void
  onToggle: () => void
  onHome: () => void
  onNew: () => void
}) {
  const language = useLanguage()
  const settings = useSettingsSurface()
  let drawerTrigger: HTMLElement | undefined
  const [state, setState] = createStore({
    drawer: undefined as HTMLElement | undefined,
    pinned: undefined as HTMLElement | undefined,
    parking: undefined as HTMLElement | undefined,
  })
  const visible = () => props.expanded || props.historyOpen
  const browse = () => {
    props.onHistoryChange(false)
    props.onHome()
  }
  const newSession = () => {
    props.onHistoryChange(false)
    props.onNew()
  }
  const settingsButton = () => (
    <IconButton
      data-action="sair-settings"
      variant="ghost-muted"
      icon={<Icon name="settings-gear" />}
      aria-label={language.t("sidebar.settings")}
      onClick={() => {
        props.onHistoryChange(false)
        if (settings.active()) settings.close()
        else settings.open()
      }}
      aria-current={props.route === "settings" ? "page" : undefined}
    />
  )

  return (
    <nav
      data-component="sair-navigation"
      data-expanded={props.expanded}
      aria-label={language.t("sair.navigation.title")}
    >
      <div class="sair-navigation-heading" data-tauri-drag-region>
        <Tooltip
          value={language.t("session.navigation.search")}
          placement="bottom"
          openDelay={200}
          inactive={!props.expanded}
        >
          <IconButton
            data-action="sair-session-search"
            icon={<Icon name="magnifying-glass" />}
            variant="ghost-muted"
            size="large"
            aria-label={language.t("session.navigation.search")}
            onClick={browse}
          />
        </Tooltip>
        <div class="sair-navigation-heading-spacer" aria-hidden="true" />
        <Tooltip
          value={language.t("session.navigation.toggle")}
          placement="bottom"
          openDelay={200}
          inactive={!props.expanded}
        >
          <IconButton
            data-action="sair-collapse-navigation"
            icon={<Icon name="layout-left" />}
            variant="ghost-muted"
            size="large"
            aria-label={language.t("session.navigation.collapse")}
            onClick={(event) => {
              props.onToggle()
              if (event.detail !== 0) return
              requestAnimationFrame(() => {
                document.querySelector<HTMLElement>('[data-action="sair-sidebar-toggle"]')?.focus()
              })
            }}
          />
        </Tooltip>
      </div>
      <div class="sair-navigation-actions">
        <Button
          type="button"
          variant="ghost"
          data-action="sair-new-session"
          aria-label={language.t("sair.navigation.new")}
          onClick={newSession}
        >
          <Icon name="new-session" />
          <span>{language.t("sair.navigation.new")}</span>
        </Button>
      </div>
      <Dialog open={props.historyOpen} onOpenChange={props.onHistoryChange}>
        <Dialog.Portal>
          <Dialog.Overlay data-component="sair-navigation-overlay" />
          <Dialog.Content
            data-component="sair-session-drawer"
            onOpenAutoFocus={() => {
              drawerTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : undefined
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault()
              if (drawerTrigger?.isConnected && drawerTrigger.getClientRects().length) drawerTrigger.focus()
            }}
          >
            <div class="sair-drawer-heading">
              <Dialog.Title>{language.t("sair.navigation.sessions")}</Dialog.Title>
              <IconButton
                data-action="sair-drawer-search"
                icon={<Icon name="magnifying-glass" />}
                variant="ghost-muted"
                aria-label={language.t("session.navigation.search")}
                onClick={browse}
              />
              <IconButton
                data-action="sair-new-session"
                icon={<Icon name="new-session" />}
                variant="ghost-muted"
                aria-label={language.t("command.session.new")}
                onClick={newSession}
              />
              <Dialog.CloseButton
                as={IconButton}
                variant="ghost-muted"
                icon={<Icon name="close" />}
                aria-label={language.t("common.close")}
              />
            </div>
            <div class="sair-session-navigation-pane" ref={(element) => setState("drawer", element)} />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
      <div
        class="sair-session-navigation-pane"
        hidden={!props.expanded}
        ref={(element) => setState("pinned", element)}
      />
      <div hidden ref={(element) => setState("parking", element)} />
      <Show when={state.parking}>
        <Portal
          mount={props.historyOpen && state.drawer ? state.drawer : props.expanded ? state.pinned : state.parking}
        >
          <div class="sair-session-navigation-content">
            <div class="sair-session-navigation-collections">
              <SidebarCollections enabled={visible()} onNavigate={() => props.onHistoryChange(false)} />
            </div>
            <div class="sair-navigation-footer" data-expanded="true">
              <SairAccountBar compact expanded />
              <Tooltip value={language.t("sidebar.settings")} placement="top">
                {settingsButton()}
              </Tooltip>
            </div>
          </div>
        </Portal>
      </Show>
    </nav>
  )
}
